/**
 * RankedCellsInline — the catalogue-wide ranking, rendered IN the chat
 * transcript at the position `rank_cells_for_application` was called.
 *
 * Why this exists: the ranker's `ranked_cells_table` artifact used to be
 * visible only inside the guided-flow split-view canvas. The guided flow was
 * switched off on 2026-08-31, but the artifact kept forcing that canvas open
 * (`chatStore.canvasSurfaceActive`) — so the user got the flow's chrome
 * (Requirements rail, Plot/Table/Report tabs) around a flow that is not
 * running. The canvas trigger is gone; the ranking now renders here, like
 * every other tool output (compare tables, aggregations, plots).
 *
 * The table itself is the SAME `RankedCellsTable` the canvas uses — it reads
 * only `manualPicks` (a plain chat signal) and its own props, never the pinned
 * artifact / canvas tab, so it mounts unchanged outside the canvas.
 *
 * What this wrapper adds is the TRANSPARENCY line: the reduction funnel
 * ("11 of 234 cells match — 223 removed: …") plus any relaxation offers and
 * degraded gates. That line is the anti-hallucination signal — it is why Ion
 * can no longer report a hand-picked shortlist's ceiling as the catalogue's.
 * The Ragone scatter deliberately does NOT render inline: it is the flow's
 * main view, not the answer to "which cell should I use".
 */

import { RankedCellsTable } from './canvas/RankedCellsTable.jsx'

/**
 * "234 in catalogue → 156 after reduce → 11 meet requirements → 11 shown".
 *
 * Built from `plot_data.funnel` (catalogue_total / after_reduce) plus the
 * plot's own `matched` count and the artifact's `rows_shown`. Every stage is
 * optional — a ranking without a funnel still gets the match count. Pure;
 * exported for tests. Returns null when nothing is known.
 */
export function formatFunnelLine(data) {
  if (!data || typeof data !== 'object') return null
  const plot = data.plot_data || {}
  const funnel = plot.funnel || {}
  const stages = []
  if (Number.isFinite(funnel.catalogue_total)) {
    stages.push(`${funnel.catalogue_total} cells in the catalogue`)
  }
  if (Number.isFinite(funnel.after_reduce)) {
    stages.push(`${funnel.after_reduce} after format/chemistry gates`)
  }
  if (Number.isFinite(plot.matched)) {
    stages.push(`${plot.matched} meet the requirements`)
  }
  const shown = Number.isFinite(data.rows_shown) ? data.rows_shown : null
  if (shown != null) stages.push(`${shown} shown`)
  return stages.length > 1 ? stages.join(' → ') : null
}

/**
 * "223 removed: pack mass over budget (180) · wrong format (43)". Mirrors the
 * canvas caption (SalesRagonePlot.formatExclusionCaption) but is re-implemented
 * here on purpose: importing that module would drag Plotly into the transcript
 * bundle for a one-line string. Pure; exported for tests.
 */
export function formatExclusionLine(data) {
  const plot = (data && data.plot_data) || {}
  const exclusions = Array.isArray(plot.exclusions) ? plot.exclusions : []
  const reasons = exclusions
    .filter((e) => e && e.removed > 0 && e.label)
    .map((e) => `${e.label} (${e.removed})`)
  if (!reasons.length) return null
  const totalRemoved = exclusions.reduce((n, e) => n + (e.removed || 0), 0)
  return `${totalRemoved} removed: ${reasons.join(' · ')}`
}

/**
 * @param {object} props
 * @param {object} props.data — the `ranked_cells_table` artifact
 */
export function RankedCellsInline({ data }) {
  if (!data) return null

  const funnelLine = formatFunnelLine(data)
  const exclusionLine = formatExclusionLine(data)
  const relaxations = Array.isArray(data.relaxations) ? data.relaxations : []
  const degraded = Array.isArray(data.degraded) ? data.degraded : []

  return (
    <div class="ion-ranked-inline">
      <RankedCellsTable data={data} />

      {(funnelLine || exclusionLine || relaxations.length > 0 || degraded.length > 0) && (
        <div class="ion-ranked-inline__transparency">
          {funnelLine && (
            <p class="ion-ranked-inline__funnel" title="How the catalogue was narrowed">
              {funnelLine}
            </p>
          )}
          {exclusionLine && (
            <p class="ion-ranked-inline__exclusions">{exclusionLine}</p>
          )}
          {relaxations.length > 0 && (
            <div class="ion-ranked-inline__relaxations">
              {/* Present ONLY when nothing passed — its presence is the signal
                  that the shortlist above is empty of passers. */}
              <span class="ion-ranked-inline__relax-lead">
                Nothing passes every hard constraint. Smallest relaxations:
              </span>
              <ul class="ion-ranked-inline__relax-list">
                {relaxations.map((r, i) => (
                  <li key={r.field || i}>{r.label || r.field}</li>
                ))}
              </ul>
            </div>
          )}
          {degraded.length > 0 && (
            <p class="ion-ranked-inline__degraded">
              Not applied:{' '}
              {degraded
                .map((d) => `${d.metric} (${d.reason || d.role || 'dropped'})`)
                .join(' · ')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
